import { useEffect } from 'react';
import { useState, useRef, FormEvent, useCallback, useMemo } from 'react';

import { FiSearch } from 'react-icons/fi';
import { IoIosArrowBack, IoMdArchive } from 'react-icons/io';
import { MdSettingsBackupRestore } from 'react-icons/md';
import { MdFilterList } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import { toast } from 'react-toastify';

import { IconPieChartSvg } from '~/assets/icons';
import { Layout } from '~/components';
import { PowerCreator } from '~/models/PowerCreator';
import api from '~/services/api';

import { Card } from './Card';
import Modal from './Modal';

import {
  ModalTitle,
  CheckboxWrapper,
  ModalButton,
  Header,
  HeaderWrapper,
  HeaderLabel,
  Container,
  PageHeader,
  ContentRight,
  InputContainer,
  PageHeaderButtons,
  CardContainer,
  CheckButton,
  CheckboxSet,
  AlertContainer,
  NoDataFound,
} from './styles';

type PowerCreatorsRequestData = {
  data: PowerCreator[];
};

const MetLibrary: React.FC = () => {
  const { goBack, push } = useHistory();

  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [modalFilterIsOpen, setModalFilterIsOpen] = useState(false);
  const [selectedMethodBox, setSelectedMethodBox] = useState<number>();
  useState<number[]>([1]);
  const [metLibraries, setMetLibraries] = useState<PowerCreator[]>([]);
  const [filteredMetLibraries, setFilteredMetLibraries] = useState<
    PowerCreator[]
  >([]);

  // load libraries
  const loadLibraries = useCallback(async () => {
    const { data } = await api.get<PowerCreatorsRequestData>('power-creators', {
      params: {
        filed: false,
        perpage: 9999,
      },
    });

    setMetLibraries(data.data);

    setLoading(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    loadLibraries();
  }, [loadLibraries]);

  useEffect(() => {
    setFilteredMetLibraries(metLibraries);
  }, [metLibraries]);

  useEffect(() => {
    if (searchValue === '') {
      setFilteredMetLibraries(metLibraries);
      return;
    }

    // eslint-disable-next-line array-callback-return, consistent-return
    const data = metLibraries.filter((metLibrary) => {
      const lowerCaseSearchValue = searchValue.toLowerCase();
      const { tranning_method } = metLibrary;
      if (tranning_method) {
        const lowerCaseTranningMethod = tranning_method.toLowerCase();
        return lowerCaseTranningMethod?.includes(lowerCaseSearchValue);
      }
    });

    setFilteredMetLibraries(data);
  }, [metLibraries, searchValue]);

  const checkBoxMethodOptions = useMemo(() => {
    return [
      { id: 1, label: '2 tempos' },
      { id: 2, label: '3 tempos' },
      { id: 3, label: 'breakdown' },
      { id: 4, label: 'bulk' },
      { id: 5, label: 'bombear' },
      { id: 6, label: 'cadência 40 20' },
      { id: 7, label: 'carga progressiva' },
      { id: 8, label: 'carga regressiva' },
      { id: 9, label: `don't stop` },
      { id: 10, label: 'propset' },
      { id: 11, label: 'escada' },
      { id: 12, label: 'flushing' },
      { id: 13, label: 'FST' },
      { id: 14, label: 'inundação' },
      { id: 15, label: 'isodinâmico' },
      { id: 16, label: 'numbness' },
      { id: 17, label: 'oxford' },
    ];
  }, []);

  const CheckBoxMethod = useMemo(
    () =>
      checkBoxMethodOptions.map((item) => (
        <CheckButton
          key={item.id}
          isSelected={selectedMethodBox === item.id}
          onClick={() => setSelectedMethodBox(item.id)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </CheckButton>
      )),
    [checkBoxMethodOptions, selectedMethodBox],
  );

  const handleSearch = useCallback(async (e: FormEvent) => {
    console.log(e);
  }, []);

  const handleNavigateToArchive = useCallback(() => {
    push('/professional/lab/met-library/archive');
  }, [push]);

  const handleNavigateToImportMethod = useCallback(() => {
    push('/professional/lab/met-library/import-methods');
  }, [push]);

  const handleRestoreMethods = useCallback(async () => {
    try {
      setLoading(true);

      await api.delete(`/power-creators/all/restore`);

      toast.success('Métodos restaurados com sucesso!');

      await loadLibraries();

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log('error:', error);
      toast.error('Houve um erro ao restaurar os métodos');
    }
  }, [loadLibraries]);

  const ModalFilter: React.FC = () => (
    <Modal
      isOpen={modalFilterIsOpen}
      setOpen={setModalFilterIsOpen}
      maxWidthStyled={621}
      maxHeightStyled={736}
    >
      <ModalTitle>Filtrar</ModalTitle>

      <CheckboxWrapper>
        <span>Métodos:</span>
        <CheckboxSet className="set">{CheckBoxMethod}</CheckboxSet>
      </CheckboxWrapper>

      <ModalButton>Aplicar</ModalButton>
    </Modal>
  );

  const deleteLibrary = useCallback(
    async (id) => {
      try {
        await api.delete(`power-creators/${id}`);

        const filteredLibraries = metLibraries.filter(
          (library) => library.id !== id,
        );

        setMetLibraries(filteredLibraries);

        toast.success(`Método deletado com sucesso!`);
      } catch (err) {
        toast.error(`Ocorreu um erro ao tentar deletar o método!`);
      }
    },
    [metLibraries],
  );

  const archiveLibrary = useCallback(
    async (id) => {
      try {
        await api.post(`power-creators/${id}`, {
          action: 'archive',
        });

        const filteredLibraries = metLibraries.filter(
          (library) => library.id !== id,
        );

        setMetLibraries(filteredLibraries);

        toast.success(`Método arquivado com sucesso!`);
      } catch (err) {
        toast.error(`Ocorreu um erro ao tentar arquivado o método!`);
      }
    },
    [metLibraries],
  );

  const duplicateLibrary = useCallback(
    async (id) => {
      const filteredLibrary = metLibraries.filter(
        (library) => library.id === id,
      );

      try {
        const { data } = await api.post(`power-creators`, filteredLibrary[0]);

        await loadLibraries();

        toast.success(`Método duplicado com sucesso!`);
      } catch (err) {
        toast.error(`Ocorreu um erro ao tentar duplicar o método!`);
      }
    },
    [metLibraries],
  );

  const CardsMemo = useMemo(
    () =>
      filteredMetLibraries.length ? (
        <CardContainer>
          {filteredMetLibraries.map((library) => (
            <Card
              key={library.id}
              data={library}
              deleteData={deleteLibrary}
              archiveData={archiveLibrary}
              duplicateData={duplicateLibrary}
            />
          ))}
        </CardContainer>
      ) : (
        <NoDataFound>
          <h1>Nenhum método encontrado!</h1>
        </NoDataFound>
      ),
    [filteredMetLibraries, deleteLibrary, archiveLibrary, duplicateLibrary],
  );

  return (
    <Layout title="I.LAB">
      <Header>
        <HeaderWrapper onClick={() => goBack()}>
          <IoIosArrowBack color="#FFDF22" size={24} />
          <HeaderLabel>Met - library</HeaderLabel>
        </HeaderWrapper>
      </Header>

      <Container>
        <PageHeader>
          <ModalFilter />

          <ContentRight>
            <InputContainer>
              <form onSubmit={handleSearch}>
                <FiSearch size={30} color="#000" />

                <input
                  type="text"
                  placeholder="Buscar por método"
                  name="search"
                  id="search"
                  value={searchValue}
                  onChange={(e) => {
                    const { value } = e.target;
                    setSearchValue(value);
                  }}
                />
              </form>
            </InputContainer>

            <PageHeaderButtons onClick={handleNavigateToImportMethod}>
              <IconPieChartSvg />

              <div>
                <p>Importar método</p>
              </div>
            </PageHeaderButtons>

            <PageHeaderButtons onClick={handleRestoreMethods}>
              <MdSettingsBackupRestore />

              <div>
                <p>Restaurar padrão</p>
              </div>
            </PageHeaderButtons>

            <PageHeaderButtons onClick={handleNavigateToArchive}>
              <IoMdArchive />

              <div>
                <p>Arquivo</p>
              </div>
            </PageHeaderButtons>
          </ContentRight>
        </PageHeader>

        {loading ? (
          <AlertContainer>
            <MoonLoader size={30} color="#FFDF22" />
          </AlertContainer>
        ) : (
          CardsMemo
        )}
      </Container>
    </Layout>
  );
};

export default MetLibrary;
